import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";

// Importações
import { apiService } from "../../../services/api";
import { User, UpdateUserData } from "../../../types/usuario";
import { toast } from "react-toastify";
import { ApiResponse } from "../../../services/api";
import { useUser } from '../../../context/userContext'; // Importar contexto
import { API_BASE_URL } from "../../../environment";

const route = all_routes;

const Profile = () => {
  const { userData, updateUserData } = useUser(); // Usar o contexto
  const [profile, setProfile] = useState<UpdateUserData>({});
  const [initialProfile, setInitialProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  // URL base da API (removido /api/v1 para usar apenas o domínio base)
  const BASE_DOMAIN = API_BASE_URL.replace('/api/v1', '');

  // Função para construir URL completa da imagem
  const getFullImageUrl = (relativePath: string | null | undefined): string | null => {
    if (!relativePath) return null;
    // Se já é uma URL completa (começa com http), retorna como está
    if (relativePath.startsWith('http')) return relativePath;
    // Se começa com /, é relativa ao domínio da API
    if (relativePath.startsWith('/')) {
      return `${BASE_DOMAIN}${relativePath}`;
    }
    // Se não tem /, adiciona / antes
    return `${BASE_DOMAIN}/${relativePath}`;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await apiService.getMyProfile();
        if (response.success && response.data) {
          setInitialProfile(response.data);
          setProfile(response.data);
          // Construir URL completa da imagem
          const fullImageUrl = getFullImageUrl(response.data.foto_perfil_url);
          console.log('URL da imagem do banco:', response.data.foto_perfil_url);
          console.log('URL completa construída:', fullImageUrl);
          if (fullImageUrl) {
            setImagePreview(fullImageUrl);
          }
          
          // Atualizar o contexto global no carregamento inicial
          updateUserData({
            name: response.data.nome,
            cargo: response.data.cargo || undefined,
            foto_perfil_url: fullImageUrl || undefined
          });
        } else {
          toast.error(response.message || "Não foi possível carregar os dados do perfil.");
        }
      } catch (error) {
        console.error(error);
        toast.error("Ocorreu um erro ao buscar o perfil.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const objectUrl = URL.createObjectURL(file);
      console.log('Nova imagem selecionada:', file.name);
      console.log('Object URL criada:', objectUrl);
      
      setProfile((prev) => ({ ...prev, foto_perfil: file, remover_foto: false }));
      setImagePreview(objectUrl);
      setSelectedFileName(file.name);
    }
  };
  
  const handleRemoveImage = () => {
    setImagePreview(null);
    setSelectedFileName(null);
    // ABORDAGEM 1: Enviar string vazia ou null explícito
    setProfile((prev) => ({ 
      ...prev, 
      foto_perfil: null,
      foto_perfil_url: null, // Também limpar a URL
      remover_foto: true 
    }));
    const inputFile = document.getElementById('imag') as HTMLInputElement;
    if(inputFile) inputFile.value = "";
    
    console.log('Imagem marcada para remoção'); // DEBUG
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    console.log('Dados sendo enviados:', profile); // DEBUG - ver o que está sendo enviado
    
    try {
      const response = await toast.promise(
        apiService.updateMyProfile(profile),
        {
          pending: 'Salvando alterações...',
          success: 'Perfil atualizado com sucesso!',
          error: 'Não foi possível salvar as alterações.'
        }
      );

      // Atualizar o estado após sucesso
      if (response.success && response.data) {
        setInitialProfile(response.data);
        setProfile(response.data);
        // Construir URL completa da nova imagem
        const fullImageUrl = getFullImageUrl(response.data.foto_perfil_url);
        setImagePreview(fullImageUrl);
        setSelectedFileName(null); // Limpar nome do arquivo selecionado
        
        console.log('Resposta da API após salvar:', response.data); // DEBUG
        
        // Atualizar o contexto global do usuário
        console.log('Atualizando contexto com:', {
          name: response.data.nome,
          cargo: response.data.cargo || undefined,
          foto_perfil_url: fullImageUrl || undefined
        }); // DEBUG
        
        updateUserData({
          name: response.data.nome,
          cargo: response.data.cargo || undefined,
          foto_perfil_url: fullImageUrl || undefined
        });
      }
    } catch (error) {
      // O toast.promise já trata o erro, então só precisamos fazer log se necessário
      console.error('Erro ao atualizar perfil:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialProfile) {
      setProfile(initialProfile);
      const fullImageUrl = getFullImageUrl(initialProfile.foto_perfil_url);
      setImagePreview(fullImageUrl);
      setSelectedFileName(null);
    }
  }

  if (loading) {
    return <div className="page-wrapper"><div className="content"><p>Carregando perfil...</p></div></div>;
  }
  
  if (!initialProfile) {
    return <div className="page-wrapper"><div className="content"><p>Não foi possível encontrar os dados do perfil. Verifique o console para mais detalhes.</p></div></div>;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-sm-4"><h4 className="page-title">Meus Dados</h4></div>
                <div className="col-sm-8 text-sm-end"><div className="head-icons"><CollapseHeader /></div></div>
              </div>
            </div>
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom">
                  <li className="nav-item me-3"><Link to={route.profile} className="nav-link px-0 active"><i className="ti ti-settings-cog" /> Configurações Gerais</Link></li>
                </ul>
              </div>
            </div>
            <div className="row">
              <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                <div className="card">
                  <div className="card-body">
                    <div className="settings-sidebar">
                      <h4 className="fw-semibold mb-3">Configurações Gerais</h4>
                      <div className="list-group list-group-flush settings-sidebar"><Link to={route.profile} className="fw-medium active">Perfil</Link></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <h4 className="fw-semibold mb-3">Configurações do Perfil</h4>
                    <form onSubmit={handleSubmit}>
                      <div className="border-bottom mb-3 pb-3">
                        <h5 className="fw-semibold mb-1">Informações do Funcionário</h5>
                        <p>Forneça as informações abaixo</p>
                      </div>
                      <div className="mb-3">
                        <div className="profile-upload">
                          <div className="profile-upload-img" style={{ 
                            position: 'relative', 
                            width: '120px', 
                            height: '120px', 
                            border: '2px dashed #ddd', 
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            backgroundColor: '#f8f9fa'
                          }}>
                            {!imagePreview && (
                              <span style={{ color: '#6c757d', fontSize: '24px' }}>
                                <i className="ti ti-photo" />
                              </span>
                            )}
                            {imagePreview && (
                              // Usar img nativo para preview de arquivos locais (blob:)
                              imagePreview.startsWith('blob:') ? (
                                <img
                                  id="ImgPreview"
                                  src={imagePreview}
                                  alt="imagem de perfil"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '6px'
                                  }}
                                  onLoad={() => {
                                    console.log('Preview de arquivo local carregado:', imagePreview);
                                  }}
                                />
                              ) : (
                                // Usar ImageWithBasePath para URLs do servidor
                                <ImageWithBasePath
                                  id="ImgPreview"
                                  src={imagePreview}
                                  alt="imagem de perfil"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '6px'
                                  }}
                                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                    console.error('Erro ao carregar imagem:', imagePreview);
                                    e.currentTarget.src = "assets/img/profiles/avatar-02.jpg";
                                  }}
                                  onLoad={() => {
                                    console.log('Imagem do servidor carregada:', imagePreview);
                                  }}
                                />
                              )
                            )}
                            {!imagePreview && (
                              <ImageWithBasePath
                                id="ImgDefault"
                                src="assets/img/profiles/avatar-02.jpg"
                                alt="imagem padrão"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  opacity: '0.3'
                                }}
                              />
                            )}
                            {imagePreview && (
                              <button 
                                type="button" 
                                id="removeImage1" 
                                className="profile-remove" 
                                onClick={handleRemoveImage}
                                title="Remover imagem"
                                style={{
                                  position: 'absolute',
                                  top: '5px',
                                  right: '5px',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  border: 'none',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px'
                                }}
                              >
                                <i className="feather-x" />
                              </button>
                            )}
                          </div>
                          <div className="profile-upload-content">
                            <label className="profile-upload-btn">
                              <i className="ti ti-file-broken" /> 
                              {imagePreview ? 'Alterar Imagem' : 'Enviar Arquivo'}
                              <input 
                                type="file" 
                                id="imag" 
                                className="input-img" 
                                accept="image/png, image/jpeg, image/gif" 
                                onChange={handleImageChange} 
                              />
                            </label>
                            <p>JPG, GIF ou PNG. Tamanho máximo de 800K</p>
                            
                            {/* Feedback visual quando há imagem do banco */}
                            {imagePreview && !selectedFileName && (
                              <div className="text-info mt-2">
                                <i className="ti ti-photo-check" /> Imagem atual do perfil
                              </div>
                            )}
                            
                            {/* Feedback visual quando nova imagem é selecionada */}
                            {selectedFileName && (
                              <div className="alert alert-success mt-2 py-2">
                                <i className="ti ti-upload" /> <strong>Nova imagem selecionada:</strong><br/>
                                <small>{selectedFileName}</small>
                              </div>
                            )}
                            
                            {/* Feedback quando não há imagem */}
                            {!imagePreview && !selectedFileName && (
                              <div className="text-muted mt-2">
                                <i className="ti ti-photo-off" /> Nenhuma imagem selecionada
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="border-bottom mb-3">
                        <div className="row">
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">Nome <span className="text-danger">*</span></label><input type="text" className="form-control" name="nome" value={profile.nome || ''} onChange={handleChange} required /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">Sobrenome</label><input type="text" className="form-control" name="sobrenome" value={profile.sobrenome || ''} onChange={handleChange} /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">Email (Login)</label><input type="text" className="form-control" value={initialProfile.email} readOnly disabled /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">Cargo</label><input type="text" className="form-control" name="cargo" value={profile.cargo || ''} onChange={handleChange} /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">Número de Telefone</label><input type="text" className="form-control" name="telefone_celular" value={profile.telefone_celular || ''} onChange={handleChange} /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">Data de Nascimento</label><input type="date" className="form-control" name="data_nascimento" value={profile.data_nascimento || ''} onChange={handleChange} /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">CPF</label><input type="text" className="form-control" name="cpf" value={profile.cpf || ''} onChange={handleChange} /></div></div>
                          <div className="col-md-6"><div className="mb-3"><label className="form-label">RG</label><input type="text" className="form-control" name="rg" value={profile.rg || ''} onChange={handleChange} /></div></div>
                        </div>
                      </div>
                      <div>
                        <button type="button" className="btn btn-light me-2" onClick={handleCancel} disabled={saving}>Cancelar</button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar Alterações'}</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;